import { CurrencyPipe, LowerCasePipe, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { BadgeComponent, ButtonComponent, ProgressBarComponent } from '@core/ui-kit/components';
import { CheckboxCircleFillComponent } from '@core/ui-kit/components/icons/checkbox-circle-fill.component';
import { environment } from '@environments/environment';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Product } from '@shared/data-access/data-access-payment';

type FlowStep = 0 | 1 | 2;

/** `first` gates the steps in order; `repeat` lets the buyer move between plan and add-ons freely. */
export type FlowMode = 'first' | 'repeat';

/** One selectable billing cadence within the Cyber exposure report add-on. */
interface CyberCadence {
  id: string;
  product: Product;
  label: string;
  priceMinor: number;
  priceType: 'one_time' | 'recurring';
  selected: boolean;
}

/** A standalone AI add-on product (independent on/off toggle). */
interface AiCard {
  id: string;
  product: Product;
  title: string;
  description: string[];
  badge: string | null;
  priceMinor: number;
  priceType: 'one_time' | 'recurring';
  selected: boolean;
}

/** Local brand assets keyed by lowercased Stripe product name. */
const PLAN_LOGOS: Record<string, string> = {
  core: '/assets/images/plans/plan-core.svg',
  professional: '/assets/images/plans/plan-professional.svg',
  premium: '/assets/images/plans/plan-premium.svg',
  enterprise: '/assets/images/plans/plan-enterprise.svg',
};

@Component({
  selector: 'app-plan-addon-flow',
  imports: [
    TranslocoPipe,
    LowerCasePipe,
    UpperCasePipe,
    CurrencyPipe,
    NgTemplateOutlet,
    ButtonComponent,
    BadgeComponent,
    CheckboxCircleFillComponent,
    ProgressBarComponent,
  ],
  templateUrl: './plan-addon-flow.component.html',
  styleUrl: './plan-addon-flow.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlanAddonFlowComponent {
  private readonly translocoService = inject(TranslocoService);

  products = input.required<Product[]>();
  cyberExposureProducts = input.required<Product[]>();
  aiAddonProducts = input.required<Product[]>();
  mode = input<FlowMode>('first');

  checkout = output<{ plan: Product | null; addons: Product[] }>();

  enterprisePlanEmail = environment.stripe.enterprisePlanEmail;

  step = signal<FlowStep>(0);
  selectedPlan = signal<Product | null>(null);
  selectedCyberProduct = signal<Product | null>(null);
  selectedAiAddonIds = signal<Set<string>>(new Set());

  private readonly allSteps = [
    'membership.plans.flow.step.plan',
    'membership.plans.flow.step.addons',
    'membership.plans.flow.step.confirm',
  ];

  isRepeat = computed(() => this.mode() === 'repeat');

  // A repeat purchase shows two freely navigable tabs; the summary is reached from the
  // footer instead of being a third tab, and the header steps aside while it is open.
  visibleSteps = computed(() => (this.isRepeat() ? this.allSteps.slice(0, 2) : this.allSteps));
  showStepHeader = computed(() => !(this.isRepeat() && this.step() === 2));

  isEnterpriseSelected = computed(() => this.selectedPlan()?.metadata['checkout'] === 'false');

  showCyberPicker = computed(() => {
    if (this.cyberExposureProducts().length === 0) {
      return false;
    }

    const plan = this.selectedPlan();

    // A repeat buyer can be on the add-on tab with no plan chosen: the report is on sale
    // in its own right, so the picker shows.
    if (!plan) {
      return true;
    }

    return plan.metadata['checkout'] !== 'false' && plan.metadata['number_of_included_cyber_report'] === '0';
  });

  includedCyberCount = computed(() => {
    const count = this.selectedPlan()?.metadata['number_of_included_cyber_report'];
    return count && count !== '0' ? count : null;
  });

  selectedAiAddons = computed(() =>
    this.aiAddonProducts().filter((product) => this.selectedAiAddonIds().has(product.id)),
  );

  selectedAddons = computed(() => {
    const cyber = this.selectedCyberProduct();
    return [...(cyber ? [cyber] : []), ...this.selectedAiAddons()];
  });

  hasSelectableAddons = computed(
    () => !this.isEnterpriseSelected() && (this.showCyberPicker() || this.aiAddonProducts().length > 0),
  );

  cyberGroupTitle = computed(() => this.translocoService.translate('membership.plans.flow.addons.cyber.title'));

  // Cyber exposure report is ONE add-on with several billing cadences → render as one section
  // with a single card holding a radio-style cadence picker (incl. "No thanks"), not N cards
  // that read like separate add-ons.
  cyberCadences = computed<CyberCadence[]>(() =>
    this.cyberExposureProducts().map((product) => ({
      id: product.id,
      product,
      label: product.name,
      priceMinor: product.defaultPrice.unitAmount,
      priceType: product.defaultPrice.type,
      selected: this.selectedCyberProduct()?.id === product.id,
    })),
  );

  // `list_order` is a Stripe metadata field the backend only sorts by for the SUBSCRIPTION
  // category, so the add-ons are ordered here instead.
  aiCards = computed<AiCard[]>(() =>
    [...this.aiAddonProducts()]
      .sort((a, b) => Number(a.metadata['list_order'] ?? 0) - Number(b.metadata['list_order'] ?? 0))
      .map((product) => ({
        id: product.id,
        product,
        title: product.name,
        description: this.descriptionItems(product),
        badge: product.metadata['addon_badge'] ?? null,
        priceMinor: product.defaultPrice.unitAmount,
        priceType: product.defaultPrice.type,
        selected: this.selectedAiAddonIds().has(product.id),
      })),
  );

  currency = computed(
    () =>
      this.selectedPlan()?.defaultPrice.currency ??
      this.cyberExposureProducts()[0]?.defaultPrice.currency ??
      this.aiAddonProducts()[0]?.defaultPrice.currency ??
      'gbp',
  );

  planPriceMinor = computed(() =>
    this.isEnterpriseSelected() ? 0 : (this.selectedPlan()?.defaultPrice.unitAmount ?? 0),
  );

  addonsPriceMinor = computed(() =>
    this.selectedAddons().reduce((sum, addon) => sum + addon.defaultPrice.unitAmount, 0),
  );

  totalPriceMinor = computed(() => this.planPriceMinor() + this.addonsPriceMinor());

  // In repeat mode the buyer can reach the footer having chosen nothing at all; one rule
  // covers every tab rather than a special case per screen.
  isCartEmpty = computed(() => !this.selectedPlan() && this.selectedAddons().length === 0);

  showFooter = computed(() => this.isRepeat() || !!this.selectedPlan());

  addonDisplayName(product: Product): string {
    const isCyber = this.cyberExposureProducts().some((cyberProduct) => cyberProduct.id === product.id);
    if (isCyber) {
      return `${this.cyberGroupTitle()} — ${product.name}`;
    }

    return product.name;
  }

  planLogo(product: Product): string | null {
    // Local per-plan colour variants first (the Stripe product images are all the same
    // dark logo); Stripe image only as fallback for plans without a local asset.
    return PLAN_LOGOS[product.name?.toLowerCase()] ?? product.images?.[0] ?? null;
  }

  choosePlan(product: Product): void {
    // In repeat mode only, clicking the already-selected plan deselects it — a repeat buyer
    // comparing prices needs a way out that isn't closing the dialog. A first purchase always
    // requires a plan, so no toggle-off exists there.
    if (this.isRepeat() && this.selectedPlan()?.id === product.id) {
      this.selectedPlan.set(null);
      // Cleared on the way out for the same reason it's cleared on the way in below: what
      // the cyber picker offers depends on the plan.
      this.selectedCyberProduct.set(null);
      return;
    }

    this.selectedPlan.set(product);

    // Only the cyber selection depends on the plan (through number_of_included_cyber_report);
    // the AI courses are plan-independent, and dropping one the buyer already picked would
    // be a silent loss in repeat mode.
    this.selectedCyberProduct.set(null);

    if (product.metadata['checkout'] === 'false') {
      this.step.set(0);
    }
  }

  backToPlan(): void {
    this.step.set(0);
  }

  continueFromPlan(): void {
    if (this.isEnterpriseSelected()) {
      return;
    }

    if (!this.isRepeat() && !this.selectedPlan()) {
      return;
    }

    this.step.set(this.hasSelectableAddons() ? 1 : 2);
  }

  /** Click-through on the step header. */
  goToStep(target: number): void {
    if (!this.isStepReachable(target)) {
      return;
    }

    this.step.set(target as FlowStep);
  }

  /**
   * Whether the header entry at `index` can be clicked in the current mode.
   *
   * This is stricter than the prototype in first-purchase mode: there, `goToStep` let you jump
   * forward to the add-ons as soon as a plan was selected, while the header styled that entry as
   * unclickable. The affordance wins — forward movement is the footer's job.
   */
  isStepReachable(index: number): boolean {
    if (this.isEnterpriseSelected()) {
      return false;
    }

    if (index === 1 && !this.hasSelectableAddons()) {
      return false;
    }

    // The summary is never reachable from the header in repeat mode — only from the footer.
    if (this.isRepeat()) {
      return index === 0 || index === 1;
    }

    return !!this.selectedPlan() && index <= this.step();
  }

  /** Whether the header entry at `index` reads as done (first purchase) or current (repeat). */
  isStepActive(index: number): boolean {
    return this.isRepeat() ? this.step() === index : this.step() >= index;
  }

  selectCyberCadence(product: Product | null): void {
    this.selectedCyberProduct.set(product);
  }

  toggleAiAddon(product: Product): void {
    this.selectedAiAddonIds.update((current) => {
      const next = new Set(current);
      if (next.has(product.id)) {
        next.delete(product.id);
      } else {
        next.add(product.id);
      }
      return next;
    });
  }

  skipAddons(): void {
    this.selectedCyberProduct.set(null);
    this.selectedAiAddonIds.set(new Set());
    this.step.set(2);
  }

  proceedToConfirm(): void {
    this.step.set(2);
  }

  descriptionItems(product: Product): string[] {
    if (!product.description) {
      return [];
    }

    return product.description.split('|').map((item) => item.trim());
  }

  confirm(): void {
    if (this.isEnterpriseSelected() || this.isCartEmpty()) {
      return;
    }

    this.checkout.emit({ plan: this.selectedPlan(), addons: this.selectedAddons() });
  }
}
